/**
 * Outgoing packets.
 *
 * Every packet below is byte-for-byte the one PlayChess sends (line refs are
 * src/Screens/PlayChess/index.js); the SDK fills `username`/`userId` from
 * `socket.username` when we leave them unset, and encodes only types whose
 * schema is registered (messages.ts + useGameplayHandlers do that).
 *
 * Actions read `getState()` SYNCHRONOUSLY — that is what makes
 * `actions.move` correct as the board's `onPieceDropExtension`, even when the
 * board replays a premove from one of its own effects.
 */
import {
  bridgeResetArena,
  bridgeSetArenaIsJoined,
  bridgeSetJoinFailure,
} from './gamePlayStoreWrites';
import {sanFor} from './chess/san';
import {canRestrict} from './chess/touchPiece';
import {JoinConflict} from './chess/tournamentJoin';
import {isThinkingGated, selectBoardSource} from './selectors';
import {
  LiveGameState,
  OutgoingChallenge,
  START_FEN,
  TournamentMode,
  WatchPlayer,
  id,
  initialArena,
  initialGame,
  initialTournament,
  initialTournamentJoin,
  initialWatch,
} from './state';

/** `new Date().getTime().toString()` everywhere in PlayChess. */
const requestId = (): string => new Date().getTime().toString();

/**
 * Outbound types the V2 lane actually sends today. The sender-parity test
 * compares this against every `send_msg('<type>'` in PlayChess; anything not
 * here yet must sit in `__tests__/parity/allowlist.ts` until its milestone.
 */
export const SENDS: string[] = [
  'join_game_request',
  'leave_table_request',
  'player_move',
  'fetch_game_state',
  'end_game_confirmed',
  // M2 Task B: challenge / cancel / rematch all ride ONE wire type
  'challenge_game_request',
  'online_players_search',
  // A3a: the tournament / watch / arena families
  'join_tournament_request',
  'withdraw_tournament_request',
  'get_tournament_status',
  'stop_watching_tournament_request',
  'watch_tournament_game_request',
  'stop_watching_tournament_game_request',
  'arena_resume_request',
  'arena_pause_request',
  'arena_status_request',
  'arena_berserk_request',
];

/** the four arena request types, in the order the old screen's helpers send. */
export const ARENA_REQUESTS = [
  'arena_resume_request',
  'arena_pause_request',
  'arena_status_request',
  'arena_berserk_request',
] as const;

/**
 * What `joinTournament` resolves with. It never REJECTS: the old screen rejects
 * an Error on failure and every call site swallows it (`.catch(() => {})`,
 * L4975), so the answer is modelled as data — `code` is the old
 * `joinTnrSuccess` (0 = transient/timeout, 1 = show the failure modal, 2 =
 * joined or a harmless duplicate).
 */
export type JoinTournamentResult = {
  ok: boolean;
  code: 0 | 1 | 2;
  message: string | null;
  conflict: JoinConflict | null;
};

/**
 * A FINISHED game fetched over HTTP and shown through the watch slice —
 * `actions.loadWatchReplay`'s argument.
 *
 * The old screen has two sources for it and pours both into the very same
 * `watch*` state a live spectate uses: the redis-first arena snapshot
 * (`/arenaGameReplay`, `openArenaGameByTable` L4665-4734) and the DB-uuid replay
 * (`game/get_games` off `setGameId`, L6216-6360). The FETCH is platform-side
 * (the app hook `pages/tournament/useGameReplay.ts`); only the shaped snapshot
 * reaches the store, so this core stays transport-free.
 */
export type WatchReplaySnapshot = {
  /** what marks the rail state as a watch — a game uuid, or `table:<id>` */
  replayGameId: string;
  tableId?: number | null;
  startFen: string;
  /** the position to show; the final one for a finished game */
  fen: string;
  prevFen?: string | null;
  /** SAN list, full */
  moves: string[];
  white: WatchPlayer | null;
  black: WatchPlayer | null;
  whiteTimeMs: number;
  blackTimeMs: number;
  currentPlayer?: string | null;
  /** the server's game-state enum; 8 = finished, which is the default */
  state?: number | null;
};

/** PlayChess L5000-5002: a join with no answer after 10 s is transient. */
export const JOIN_TOURNAMENT_TIMEOUT_MS = 10000;

/** what `challengeGame` needs; every caller (lobby CTA, rematch) builds this. */
export type ChallengeInput = {
  /** bare user id — '0' for Caissa (ChallengeCaissaComponent L144) */
  opponentId: string;
  opponentName: string;
  tcMs: number;
  incMs: number;
  /** the white player's BARE user id, or '' to let the server toss */
  white?: string;
  fen?: string;
  gamePlayMode?: string;
};

export type ActionDeps = {
  getState: () => LiveGameState;
  setState: (updater: (s: LiveGameState) => LiveGameState) => void;
  /** the cc_ws_client instance, or null before SocketProvider created it. */
  getSocket: () => any;
};

export type LiveGameActions = ReturnType<typeof createActions>;

export function createActions(deps: ActionDeps) {
  const {getState, setState, getSocket} = deps;

  /**
   * The join_tournament_request promises still in flight, keyed by the packet's
   * requestId — the old screen's `pendingTournamentJoinsRef` (L4993-5023).
   *
   * `join_tournament_response` echoes the requestId, which is what makes this
   * request/response pair possible over a fire-and-forget socket. It is settled
   * from `settleInbound`, called by the store's `applyMessage` AFTER the pure
   * reduce — the reducer itself may not resolve a promise.
   */
  const pendingJoins = new Map<
    string,
    {resolve: (result: JoinTournamentResult) => void; timeoutId: any}
  >();

  const settleJoin = (key: string, result: JoinTournamentResult): void => {
    const pending = pendingJoins.get(key);
    if (!pending) {
      return;
    }
    clearTimeout(pending.timeoutId);
    pendingJoins.delete(key);
    pending.resolve(result);
  };

  const send = (type: string, packet: any): boolean => {
    const socket = getSocket();
    if (!socket) {
      // PlayChess pops a window.alert here (L3124-3128); the V2 screen shows
      // the connection banner instead, so this is a silent no-op.
      return false;
    }
    socket.send_msg(type, packet);
    return true;
  };

  /** the table a GAMEPLAY packet belongs to (move/draw/resign/abort). */
  const gameTableId = (s: LiveGameState): number => s.game.tableId ?? 0;

  /**
   * The table a seek can still be cancelled on: `join_game_response` hands one
   * back before any game exists, and it must win over a finished game's id
   * (PlayChess overwrites the same `tableId` state — L2459-2463, L3096-3110).
   */
  const seekTableId = (s: LiveGameState): number =>
    s.lobby.seekTableId ?? s.game.tableId ?? 0;

  /** player_move carries the move AND its resign/abort/draw/touch variants. */
  const sendPlayerMove = (variant: string, move: string): boolean => {
    const s = getState();
    return send('player_move', {
      requestId: requestId(),
      username: s.session.me,
      type: variant,
      tableId: gameTableId(s),
      move,
    });
  };

  /**
   * OUT challenge_game_request — PlayChess `challengeGame` L3159-3202.
   *
   * The packet carries all 11 schema fields (`black` is always '' on the way
   * out, exactly as L3183 sends it). Two DELIBERATE differences from the old
   * screen, both reported with the milestone:
   *  - `opponent` is `<id>:<name>`; L3181 appends `opponentDisplayName` to an
   *    already-packed value, so the old wire value is `<id>:<name>:<stale
   *    name from the PREVIOUS game>` (or a trailing ':' after a username
   *    search, which clears that state at L3581). The server splits on ':'
   *    and uses [0]/[1] only (joinLeaveProcessor L95-101), so the extra
   *    segment is inert;
   *  - `white` is a BARE user id. GameSearch L325 puts the PACKED
   *    `<id>:<name>` there when the Black chip is picked, and the server
   *    stores `white` as the table's `tossWinner` verbatim
   *    (allocateSeat.lua L19-29, L119) — so the old "Black" chip produces a
   *    table whose white player is a string that matches neither seat.
   *
   * A finished game is dropped first (L3172-3173 `setShowEndGameModal(false)`
   * / `setStarted(false)`), so the rail can leave `result` for `challengeOut`.
   */
  const challengeGame = (input: ChallengeInput): void => {
    const s = getState();
    const outgoing: OutgoingChallenge = {
      requestId: requestId(),
      opponentId: id(input.opponentId),
      opponentName: input.opponentName || '',
      tcMs: input.tcMs,
      incMs: input.incMs,
      white: input.white || '',
      fen: input.fen || START_FEN,
      gamePlayMode: input.gamePlayMode || s.lobby.gamePlayMode || 'Play',
      variantId: 1,
      sentAt: Date.now(),
    };
    send('challenge_game_request', {
      requestId: outgoing.requestId,
      type: 'challenge_game_request',
      variantId: outgoing.variantId,
      userId: s.session.me,
      opponent: `${outgoing.opponentId}:${outgoing.opponentName}`,
      playerMoveTimer: outgoing.tcMs,
      moveTimeIncrement: outgoing.incMs,
      gameState: outgoing.fen,
      white: outgoing.white,
      black: '',
      gamePlayMode: outgoing.gamePlayMode,
    });
    setState(state => ({
      ...state,
      game: state.game.phase === 'ended' ? {...initialGame} : state.game,
      lobby: {...state.lobby, phase: 'idle', seekTableId: null},
      challenge: {...state.challenge, outgoing, notice: null},
    }));
  };

  /**
   * OUT the arena envelope — PlayChess `sendArenaMessage` L4628-4649. All four
   * request types share one shape; `tableId` is meaningful ONLY on
   * `arena_berserk_request` (the server shards its dispatch on it) and is the
   * empty STRING otherwise.
   *
   * `requestId` is the same timestamp every other packet uses — the old screen
   * does not put a uuid here either (L4634), so there is no new dependency.
   */
  const sendArenaMessage = (
    type: string,
    berserkTableId?: number | string,
  ): boolean => {
    const arenaId = getState().tournament.id;
    if (!arenaId) {
      // L4631-4633: no arena, nothing to send.
      return false;
    }
    return send(type, {
      requestId: requestId(),
      type,
      arenaId: String(arenaId),
      tableId: berserkTableId != null ? String(berserkTableId) : '',
      payload: '{}',
    });
  };

  const answerDraw = (accept: boolean): void => {
    // L3660-3675
    sendPlayerMove('draw_response', accept ? 'Y' : 'N');
    setState(s => ({...s, game: {...s.game, drawOffered: false}}));
  };

  return {
    /** OUT join_game_request — PlayChess L3128-3156. */
    seek(tcMs: number, incMs: number, gamePlayMode: string = 'Play'): void {
      const s = getState();
      send('join_game_request', {
        requestId: requestId(),
        type: 'join_game_request',
        variantId: 1,
        username: s.session.me,
        message: '',
        playerMoveTimer: tcMs,
        moveTimeIncrement: incMs,
        gamePlayMode,
      });
      setState(state => ({
        ...state,
        lobby: {
          ...state.lobby,
          phase: 'seeking',
          seekTableId: null,
          tcMs,
          incMs,
          gamePlayMode,
          playerNotFound: false,
        },
      }));
    },

    /**
     * OUT leave_table_request — PlayChess L3096-3110. The server REFUSES it
     * once the table is matched (state >= 3); from there only abort/resign.
     */
    cancelSeek(): void {
      const s = getState();
      send('leave_table_request', {
        requestId: requestId(),
        type: 'leave_table_request',
        tableId: seekTableId(s),
      });
      setState(state => ({
        ...state,
        lobby: {...state.lobby, phase: 'idle', seekTableId: null},
      }));
    },

    /**
     * The 15 s search timeout — PlayChess L4113-4124: the SAME
     * `leave_table_request` as a manual cancel, plus the "players not
     * available" notice (`showPlayerNotFound`, rendered at L5650-5658). No new
     * packet: only the notice differs from `cancelSeek`.
     */
    seekExpired(): void {
      const s = getState();
      if (s.lobby.phase !== 'seeking') {
        return;
      }
      send('leave_table_request', {
        requestId: requestId(),
        type: 'leave_table_request',
        tableId: seekTableId(s),
      });
      setState(state => ({
        ...state,
        lobby: {
          ...state.lobby,
          phase: 'idle',
          seekTableId: null,
          playerNotFound: true,
        },
      }));
    },

    /**
     * The board's `onPieceDropExtension` — PlayChess `makeMove` L3608-3650.
     * Board contract: a TRUTHY return blocks the move (chessground reverts),
     * `undefined` lets it commit.
     */
    move(from: string, to: string, promotion?: string): true | undefined {
      const s = getState();
      if (s.game.phase !== 'playing') {
        return true;
      }
      if (!s.session.me || id(s.game.turnUserId) !== s.session.me) {
        // Out-of-turn moves are dropped SILENTLY by the server — never send.
        return true;
      }
      const san = sanFor(s.game.fen, from, to, promotion);
      if (!san) {
        return true;
      }
      if (s.game.drawOffered) {
        // Moving while an offer is open is an implicit decline (L3631-3637);
        // PlayChess sends the decline BEFORE the move.
        answerDraw(false);
      }
      sendPlayerMove('player_move', san);
      setState(state => ({
        ...state,
        meta: {...state.meta, pendingMoveSentAt: Date.now()},
      }));
      return undefined;
    },

    /** OUT player_resign — L3546-3559. Ignored by the server unless state 4. */
    resign(): void {
      sendPlayerMove('player_resign', 'resign');
    },

    /** OUT game_abort — L3593-3606. Only valid before the first move. */
    abort(): void {
      sendPlayerMove('game_abort', 'abort');
    },

    /** OUT draw_request — L3521-3536. */
    offerDraw(): void {
      sendPlayerMove('draw_request', 'draw');
      setState(s => ({...s, game: {...s.game, drawSent: true}}));
    },

    /** OUT draw_response 'Y' | 'N' — L3660-3675. */
    answerDraw,

    /**
     * OUT touch_piece — L4196-4208 (touch_piece_rule holders only), with the
     * FOUR guards of the old `restrictPiece` (index.tsx L6064-6071, index.js
     * L4226-4233) moved in here: the rule is on, nothing is restricted yet, the
     * piece is mine, and it has somewhere to go (`chess/touchPiece.canRestrict`,
     * the old `canMoveRestrictedPiece`). The V2 lane adds the two the old screen
     * got from the board's own state: the game must be BEING PLAYED and it must
     * be my own board, not a watch — the board hook reports every touch, so a
     * browsed/finished/watched board would otherwise restrict a piece. Fix V3
     * adds a fifth, the thinking-time gate (`isThinkingGated`): chessground
     * fires `select` even on a disabled board, so without this guard a touch
     * during the gate recorded a restriction and sent `touch_piece` though the
     * board looked locked — then stayed stuck once the gate expired, because
     * nothing re-enables a restricted board (V3's other half,
     * `clearThinkingGate`, is what ends the lock itself).
     */
    touchPiece(piece: any): void {
      const s = getState();
      if (!s.session.customRules.includes('touch_piece_rule')) {
        return;
      }
      if (isThinkingGated(s, Date.now())) {
        return;
      }
      if (s.game.restrictedPiece) {
        return;
      }
      if (s.game.phase !== 'playing' || selectBoardSource(s) !== 'live') {
        return;
      }
      if (!s.game.myColor) {
        return;
      }
      const myColor = s.game.myColor === 'white' ? 'w' : 'b';
      if (piece?.color !== myColor) {
        return;
      }
      if (!canRestrict(s.game.fen, piece.square, myColor)) {
        return;
      }
      sendPlayerMove('touch_piece', JSON.stringify(piece));
      setState(state => ({
        ...state,
        game: {...state.game, restrictedPiece: piece},
      }));
    },

    /**
     * LOCAL only. Fix V3: the thinking-time gate was armed/cleared at the
     * server's two events only (`reducers/game.ts` — move 10, game end), so
     * nothing ever turned its EXPIRY into a store transition; `isThinkingGated`
     * could sit stale-true between unrelated writes, leaving the board (and a
     * piece `touchPiece` had already restricted) locked after the dock's own
     * 250 ms tick had stopped showing "Thinking time!". `hooks/
     * useGameplayEffects` schedules this for `until + 25ms`, the old lane's
     * `setTimeout` (`PlayChess/index.js` L2688-2691) turned into a transition
     * every selector re-evaluates from. A no-op while the gate is still
     * running, so a stale/early timer can never clear a gate ahead of time.
     */
    clearThinkingGate(): void {
      const until = getState().game.thinkingGateUntil;
      if (until !== null && until <= Date.now()) {
        setState(state => ({
          ...state,
          game: {...state.game, thinkingGateUntil: null},
        }));
      }
    },

    /**
     * OUT end_game_confirmed — L3966-3981. MANDATORY: until it arrives the
     * server re-sends `game_end_response` on every fetch/reconnect.
     */
    endGameConfirmed(): void {
      const s = getState();
      send('end_game_confirmed', {
        requestId: requestId(),
        userId: s.session.me,
        type: 'end_game_confirmed',
      });
      setState(state => ({
        ...state,
        game: {...state.game, endAckNeeded: false},
      }));
    },

    /** OUT fetch_game_state — L3906-3926 (restore / resync). */
    fetchGameState(userId?: string): void {
      const s = getState();
      send('fetch_game_state', {
        requestId: requestId(),
        type: 'fetch_game_state',
        userId: userId || s.session.me,
      });
    },

    /**
     * Local only: drop the finished game so the lobby can show again. The
     * whole slice is reset (not patched) so no stale tableId/result can leak
     * into the next game. The challenge notice goes with it: the viewer is
     * acting on the lobby, so a "Challenge Rejected!" card from a minute ago
     * has said its piece (M2 spec §4bis).
     */
    resetForNewGame(): void {
      setState(state => ({
        ...state,
        game: {...initialGame},
        lobby: {...state.lobby, phase: 'idle', seekTableId: null},
        challenge: {...state.challenge, notice: null},
      }));
    },

    challengeGame,

    /**
     * OUT challenge_cancel — PlayChess `handleChallengeCancel` L3712-3735. The
     * wire TYPE is `challenge_game_request`; `type` inside the packet is what
     * makes it a cancel.
     *
     * The fields come from `challenge.outgoing`, i.e. from the challenge that is
     * actually out. The old screen rebuilds them from unrelated state instead:
     * `challengeGame` never sets `ChallengeVariantId` / `ChallengePosition` /
     * `ChallengeWhite`, so a cancel after a fresh challenge ships
     * `variantId: 0`, the DEFAULT start FEN and `black: 'Black'` (L804). The
     * server only echoes those three to the opponent's dismiss handler
     * (joinLeaveProcessor L376-390), so the difference is not observable.
     */
    cancelChallenge(): void {
      const s = getState();
      const outgoing = s.challenge.outgoing;
      if (!outgoing) {
        return;
      }
      send('challenge_game_request', {
        requestId: requestId(),
        type: 'challenge_cancel',
        variantId: outgoing.variantId,
        userId: s.session.me,
        opponent: `${outgoing.opponentId}:${outgoing.opponentName}`,
        playerMoveTimer: outgoing.tcMs,
        moveTimeIncrement: outgoing.incMs,
        gameState: outgoing.fen,
        white: outgoing.white,
        black: '',
        gamePlayMode: outgoing.gamePlayMode,
      });
      setState(state => ({
        ...state,
        challenge: {...state.challenge, outgoing: null, notice: null},
      }));
    },

    /**
     * Challenge the last opponent again — PlayChess's Rematch button
     * (L5203-5218 / L5455-5470: `challengeGame(ChallengeOpponent,
     * ChallengeMoveTimer, ChallengeIncrement, ChallengeWhite)`, i.e. no FEN, so
     * the standard start position).
     *
     * COLOURS SWAP: `game_end_response` sets `ChallengeWhite` to
     * `data.white == data.userId ? data.opponent : data.userId` (L3035-3037) —
     * whoever was NOT white gets white in the rematch. Returns false when the
     * conditions of `selectResultView.canRematch` are not met, so a caller
     * cannot send a challenge to nobody.
     */
    rematch(): boolean {
      const s = getState();
      const {opponentId, opponentName, tcMs, incMs, whiteId} = s.game;
      if (!opponentId || !tcMs) {
        return false;
      }
      const me = s.session.me;
      challengeGame({
        opponentId,
        opponentName,
        tcMs,
        incMs,
        white: id(whiteId) === me ? id(opponentId) : me,
        gamePlayMode: s.game.gamePlayMode || 'Play',
      });
      return true;
    },

    /**
     * OUT online_players_search — PlayChess `searchPlayers` L3562-3584. The
     * previous answer is dropped BEFORE the send (L3580-3581), so the picker
     * cannot offer a name from the last query while this one is in flight.
     */
    searchPlayers(pattern: string): void {
      const s = getState();
      setState(state => ({
        ...state,
        lobby: {...state.lobby, searchResults: null},
      }));
      send('online_players_search', {
        requestId: requestId(),
        userId: s.session.me,
        type: 'online_players_search',
        pattern,
      });
    },

    /**
     * LOCAL only — the `challengePacket` handshake (PlayChess L3870-3899).
     *
     * `Components/ChallengeComponent` already sent `challenge_accept_response`
     * before it navigated here, so there is nothing to send: the terms are
     * recorded (a later cancel/rematch is rebuilt from them) and
     * `start_game_response` starts the game exactly as a quick-play match does.
     */
    adoptChallengePacket(packet: any): void {
      if (!packet) {
        return;
      }
      const packed = String(packet.opponent || '');
      setState(state => ({
        ...state,
        challenge: {
          ...state.challenge,
          notice: null,
          incoming: {
            requestId: packet.requestId ? String(packet.requestId) : '',
            opponentId: id(packed.split(':')[0]),
            opponentName: packed.split(':')[1] || '',
            tcMs: Number(packet.playerMoveTimer) || 0,
            incMs: Number(packet.moveTimeIncrement) || 0,
            fen: packet.gameState || START_FEN,
            white: packet.white ? String(packet.white) : '',
            gamePlayMode: packet.gamePlayMode || 'Play',
            variantId: Number(packet.variantId) || 0,
            tableId:
              packet.tableId === undefined ? null : Number(packet.tableId),
          },
        },
      }));
    },

    /** Local only: drop the rejected/cancelled notice the lobby is showing. */
    dismissChallengeNotice(): void {
      setState(state =>
        state.challenge.notice
          ? {...state, challenge: {...state.challenge, notice: null}}
          : state,
      );
    },

    /**
     * Local only: drop the "Players not available, Try again later!" notice.
     *
     * The flag is also cleared by the next `seek`, which is what makes a
     * re-seek show the notice again; this is the user's (x) on the modal
     * (`handle_challenge_reject` -> `setShowPlayerNotFound(false)`, L3786).
     */
    dismissPlayerNotFound(): void {
      setState(state =>
        state.lobby.playerNotFound
          ? {...state, lobby: {...state.lobby, playerNotFound: false}}
          : state,
      );
    },

    /**
     * Local only: drop the `tournament_starting_soon` notice
     * (`handle_challenge_reject` -> `setShowTnrStarting(false)`, L3787). The
     * payload is kept on the slice until then, because it carries the copy.
     */
    clearStartingSoon(): void {
      setState(state =>
        state.tournament.startingSoon
          ? {...state, tournament: {...state.tournament, startingSoon: null}}
          : state,
      );
    },

    // ── Tournament (Swiss and arena) ─────────────────────────

    /**
     * LOCAL only: the tournament the surface is on. The screen calls it when the
     * user opens a tournament, because only the DETAILS response says which
     * family it is (`tournament_type === 'Circlechess_Arena'`) — the old screen
     * has `TournamentPlay` flip `ArenaStore.isArena` from exactly that field.
     *
     * An id CHANGE re-arms the arena auto-resume gate, which is the old
     * `arenaAutoResumedRef` reset (L2210-2214), and drops everything that
     * belonged to the previous tournament: its round, its join attempt, its
     * status and its standings would otherwise be read as this one's.
     */
    setTournamentContext(context: {
      id: string | null;
      mode?: TournamentMode | null;
    }): void {
      const nextId = context.id ? String(context.id) : null;
      if (nextId !== getState().tournament.id) {
        // L2245 / L6405: leaving a tournament (or switching to another one)
        // clears the platform arena store too, or B would open on A's standings.
        bridgeResetArena();
      }
      setState(state => {
        const changed = nextId !== state.tournament.id;
        if (!changed) {
          return context.mode === undefined ||
            context.mode === state.tournament.mode
            ? state
            : {
                ...state,
                tournament: {...state.tournament, mode: context.mode ?? null},
              };
        }
        return {
          ...state,
          tournament: {
            ...initialTournament,
            id: nextId,
            mode: context.mode ?? null,
          },
          arena: {...initialArena},
        };
      });
    },

    /**
     * OUT join_tournament_request — PlayChess `joinTournament` L4980-5023.
     *
     * The conflict is cleared AT SEND TIME (L4982-4987): the response and the
     * `join_tournament_conflict` message arrive in an unguaranteed order, so the
     * response handler cannot safely clear it on arrival, and a stale conflict
     * must not leak into an unrelated failure.
     *
     * The promise is settled by `settleInbound` off the echoed requestId, or
     * after {@link JOIN_TOURNAMENT_TIMEOUT_MS} with the transient answer (the old
     * screen rejects an Error there; every caller swallows it).
     */
    joinTournament(tournamentId: string): Promise<JoinTournamentResult> {
      const s = getState();
      const packet = {
        requestId: requestId(),
        type: 'join_tournament_request',
        playerId: s.session.me,
        tournamentId,
      };
      setState(state => ({
        ...state,
        tournament: {
          ...state.tournament,
          join: {
            status: 'pending',
            code: null,
            message: null,
            conflict: null,
            requestId: packet.requestId,
          },
        },
      }));
      if (!send('join_tournament_request', packet)) {
        // L4989-4992: no client = "Tournament connection is not ready."
        return Promise.resolve({
          ok: false,
          code: 0,
          message: 'Tournament connection is not ready.',
          conflict: null,
        });
      }
      return new Promise<JoinTournamentResult>(resolve => {
        const timeoutId = setTimeout(() => {
          pendingJoins.delete(packet.requestId);
          resolve({
            ok: false,
            code: 0,
            message: 'Tournament join timed out.',
            conflict: null,
          });
        }, JOIN_TOURNAMENT_TIMEOUT_MS);
        pendingJoins.set(packet.requestId, {resolve, timeoutId});
      });
    },

    /**
     * OUT withdraw_tournament_request — PlayChess `withDrawPlayer` L5025-5037.
     *
     * Fix P (2026-10-06): marks `tournament.withdraw` `pending` BEFORE
     * sending — the same shape `joinTournament` already sets on its own
     * `join` — so the UI can show "Withdrawing…" from the tap, not just once
     * a reply (or a stale REST refetch) arrives.
     */
    withdrawTournament(tournamentId: string, round: number = 0): void {
      const s = getState();
      setState(state => ({
        ...state,
        tournament: {
          ...state.tournament,
          withdraw: {status: 'pending', message: null},
        },
      }));
      send('withdraw_tournament_request', {
        requestId: requestId(),
        type: 'withdraw_tournament_request',
        playerId: s.session.me,
        tournamentId: String(tournamentId),
        round,
      });
    },

    /** OUT get_tournament_status — PlayChess L4612-4624. */
    getTournamentStatus(tournamentId?: string): void {
      const s = getState();
      const tid = tournamentId ?? s.tournament.id;
      if (!tid) {
        return;
      }
      send('get_tournament_status', {
        requestId: requestId(),
        type: 'get_tournament_status',
        tournamentId: String(tid),
      });
    },

    /**
     * OUT stop_watching_tournament_request — PlayChess `sendStopWatchingTournament`
     * L2218-2226. Leaves the tournament ROOM (not a single game): without it,
     * switching from tournament A to B leaves the socket in room A for good.
     */
    stopWatchingTournament(tournamentId?: string): void {
      const s = getState();
      const tid = tournamentId ?? s.tournament.id;
      if (!tid) {
        return;
      }
      send('stop_watching_tournament_request', {
        requestId: requestId(),
        type: 'stop_watching_tournament_request',
        tournamentId: String(tid),
      });
    },

    /**
     * LOCAL only: the refetch tick `TournamentPlay` watches. The REDUCER only
     * stamps `lastGameEndAt` for every `tournament_game_end_response`; this is
     * what the screen root's 10-15 s jittered throttle calls, so the standings
     * refetch happens once per window instead of once per broadcast
     * (PlayChess L3428-3446).
     */
    bumpGamesEnded(): void {
      setState(state => ({
        ...state,
        tournament: {
          ...state.tournament,
          gamesEnded: state.tournament.gamesEnded + 1,
        },
      }));
    },

    /**
     * LOCAL only: drop the cosmetic round-start countdown. The old screen arms it
     * in `pairingCallback` and clears it 11 s later (L3230-3232); a reducer
     * cannot hold that timer, so the screen root calls this.
     */
    clearRoundStart(): void {
      setState(state =>
        state.tournament.roundStartAt === null
          ? state
          : {
              ...state,
              tournament: {...state.tournament, roundStartAt: null},
            },
      );
    },

    /**
     * LOCAL only: the REST-proven stamp for the reload path (fix AA,
     * 2026-10-06). `bye_response` (`reducers/tournament.ts`) is volatile —
     * gone after a reload — and `tournament_status_response` carries no
     * per-player field at all, so the ONLY reload-safe proof of a bye is the
     * round's own pairings (my row with no opponent); `rail/tournament/
     * useByeProbe.ts` (web) calls this once it has read them.
     *
     * `at: null` ON PURPOSE: `hooks/useTournamentEffects.ts`'s `onBye`
     * refetches the tournament status whenever `bye.at` CHANGES, and the
     * reload that got us here already fetched it — a PROVEN bye must not
     * cause a second fetch. A no-op once `tournament.bye?.round === round`
     * (same state reference, so a live `bye_response` for this round is
     * never overwritten back to a null `at`), which also makes this safe to
     * call on every render of the probe.
     */
    proveBye(round: number): void {
      setState(state =>
        state.tournament.bye?.round === round
          ? state
          : {
              ...state,
              tournament: {
                ...state.tournament,
                bye: {round, at: null},
                // the same "never lower it" bump `byeResponse` does.
                currentRound:
                  round > (state.tournament.currentRound ?? 0)
                    ? round
                    : state.tournament.currentRound,
              },
            },
      );
    },

    /**
     * LOCAL only: the REST details are the only source for a tournament that
     * finished BEFORE this session ever opened it (fix AB, 2026-10-06) — a
     * finished tournament is no longer in the game service's live set, so no
     * socket message ever tells the core `ended`/the last round for it.
     * `is_active === false` is the SAME verdict `tournamentStatus.ts` row 1
     * already reads off `raw`; writing it here too is what makes the stage
     * card, the tab strip and the round picker agree with the Details card
     * instead of each reading a different half of the truth. `rail/
     * tournament/useTournamentDetails.ts` (web) calls this once per fetch.
     *
     * No-op unless this IS the open tournament; no-op while the REST row
     * still says the tournament is active (a live tournament has nothing
     * here the socket does not already give, and `ended` is monotonic — this
     * never CLEARS it); no-op once the core already knows at least as much
     * (same state reference either way, so this is safe to call on every
     * render of the query that feeds it).
     */
    adoptTournamentDetails(input: {
      tournamentId: string | number;
      isActive: boolean;
      rounds: number;
    }): void {
      setState(state => {
        if (String(state.tournament.id) !== String(input.tournamentId)) {
          return state;
        }
        if (input.isActive) {
          return state;
        }
        if (
          state.tournament.ended &&
          (state.tournament.currentRound ?? 0) >= input.rounds
        ) {
          return state;
        }
        return {
          ...state,
          tournament: {
            ...state.tournament,
            ended: true,
            currentRound:
              input.rounds > 0
                ? Math.max(state.tournament.currentRound ?? 0, input.rounds)
                : state.tournament.currentRound,
          },
        };
      });
    },

    /**
     * LOCAL only: the join modal has been answered / dismissed.
     *
     * `GamePlayStore.joinFailure` goes with it. `SocketProvider` writes that
     * copy for whichever screen is up (the same `join_tournament_response` the
     * reducer decoded), and the old screen CONSUMES it once it has surfaced the
     * failure (`clearJoinFailure(null)`, L790 + L813). Leaving it behind is what
     * made a stale failure from a previous run pop on the next tournament: the
     * store is persisted.
     */
    clearTournamentJoinState(): void {
      bridgeSetJoinFailure(null);
      setState(state => ({
        ...state,
        tournament: {...state.tournament, join: {...initialTournamentJoin}},
      }));
    },

    // ── Watch ────────────────────────────────────────────

    /**
     * OUT watch_tournament_game_request — PlayChess `watchTournamentGame`
     * L5083-5118.
     *
     * A watch already in flight is STOPPED FIRST (L5093-5101), or the server
     * keeps pushing the old game's turn/end events and they corrupt the newly
     * watched board. The local slice is deliberately left alone: the snapshot
     * overwrites it without a flicker.
     */
    watchTournamentGame(
      pairingKey: string,
      tournamentId: string,
      round: number,
    ): void {
      const s = getState();
      if (s.watch.tableId) {
        send('stop_watching_tournament_game_request', {
          requestId: requestId(),
          type: 'stop_watching_tournament_game_request',
          userId: s.session.me,
          tableId: s.watch.tableId,
        });
      }
      send('watch_tournament_game_request', {
        requestId: requestId(),
        type: 'watch_tournament_game_request',
        userId: s.session.me,
        pairingKey,
        tournamentId: String(tournamentId),
        round,
      });
    },

    /**
     * OUT stop_watching_tournament_game_request — PlayChess
     * `stopWatchingTournamentGame` L5120-5136. The local slice is reset here and
     * not on the server's echo: the old screen does the same (L5131-5135), so
     * leaving a watch is instant even if the echo never arrives.
     */
    stopWatchingGame(tableId?: number): void {
      const s = getState();
      const table = tableId ?? s.watch.tableId;
      if (table) {
        send('stop_watching_tournament_game_request', {
          requestId: requestId(),
          type: 'stop_watching_tournament_game_request',
          userId: s.session.me,
          tableId: table,
        });
      }
      setState(state => ({...state, watch: {...initialWatch}}));
    },

    /**
     * LOCAL only: a FINISHED game, fetched over HTTP, shown on the watch board.
     *
     * The old screen writes its ~18 `watch*` setters by hand from two different
     * fetches (`openArenaGameByTable` L4680-4724 and the `setGameId` ->
     * `get_games` effect L6240-6360); this is that write, once, so the screen
     * never touches the slice itself. `active` stays FALSE — there is no socket
     * watch room, so no turn/end event can arrive — and `clockRunningSince` is
     * null, which is what freezes both faces (`selectWatchClock`).
     * `replayGameId` is what puts the rail in 'watch' (`selectRailState`).
     */
    loadWatchReplay(snapshot: WatchReplaySnapshot): void {
      setState(state => ({
        ...state,
        watch: {
          ...initialWatch,
          active: false,
          replayGameId: String(snapshot.replayGameId),
          tableId:
            snapshot.tableId === undefined || snapshot.tableId === null
              ? null
              : Number(snapshot.tableId),
          state: snapshot.state ?? 8,
          fen: snapshot.fen || snapshot.startFen || null,
          prevFen: snapshot.prevFen ?? null,
          startFen: snapshot.startFen || null,
          moves: snapshot.moves ?? [],
          white: snapshot.white,
          black: snapshot.black,
          whiteTimeMs: snapshot.whiteTimeMs ?? 0,
          blackTimeMs: snapshot.blackTimeMs ?? 0,
          clockRunningSince: null,
          currentPlayer: snapshot.currentPlayer ?? null,
        },
      }));
    },

    // ── Arena ────────────────────────────────────────────

    sendArenaMessage,

    /**
     * OUT arena_resume_request — L4650-4652. Doubles as "pool me": it is the
     * Resume button AND the auto-resume. Raising `autoResumed` here is the old
     * `arenaAutoResumedRef.current = true` (L4759), so a joined idle player is
     * re-pooled at most once per visit and a refresh cannot loop.
     */
    arenaResume(): void {
      sendArenaMessage('arena_resume_request');
      setState(state => ({
        ...state,
        arena: {...state.arena, autoResumed: true},
      }));
    },

    /** OUT arena_pause_request — L4653-4655. */
    arenaPause(): void {
      sendArenaMessage('arena_pause_request');
    },

    /** OUT arena_status_request — L4656-4658. */
    arenaStatus(): void {
      sendArenaMessage('arena_status_request');
    },

    /** OUT arena_berserk_request — L4659-4661. The ONE type with a tableId. */
    arenaBerserk(tableId?: number | string): void {
      const s = getState();
      sendArenaMessage(
        'arena_berserk_request',
        tableId ?? s.game.tableId ?? undefined,
      );
    },

    /** LOCAL only: the berserk refusal notice has been shown. */
    dismissBerserkRefusal(): void {
      setState(state =>
        state.arena.berserkRefusal === null
          ? state
          : {...state, arena: {...state.arena, berserkRefusal: null}},
      );
    },

    /**
     * LOCAL only: mirror the platform's arena join flag. The details response
     * (`is_joined`) and a successful in-session join are its sources, both of
     * which live on the screen — it gates the auto-resume so a mere VISITOR is
     * never pooled (PlayChess L4752).
     */
    setArenaJoined(value: boolean): void {
      if (value !== getState().arena.isJoined) {
        bridgeSetArenaIsJoined(value);
      }
      setState(state =>
        state.arena.isJoined === value
          ? state
          : {...state, arena: {...state.arena, isJoined: value}},
      );
    },

    /**
     * The ONE non-pure step of the inbound path, called by the store's
     * `applyMessage` AFTER the pure reduce: it settles the request/response
     * promises the actions above are holding. It writes no state and sends
     * nothing, so the reducers stay pure.
     */
    settleInbound(type: string, data: any): void {
      // NOTE (A3a review): the resolved `conflict` is whatever the store knows
      // AT SETTLE TIME. `join_tournament_conflict` is a separate message and
      // may land AFTER the response; the store's `tournament.join.conflict` is
      // always right, so a conflict UI must read it reactively (selectTournamentJoin)
      // and never rely on the promise value alone.
      if (type !== 'join_tournament_response' || !data?.requestId) {
        return;
      }
      const {join} = getState().tournament;
      settleJoin(String(data.requestId), {
        ok: join.code === 2,
        code: (join.code ?? 0) as 0 | 1 | 2,
        message: join.message,
        conflict: join.conflict,
      });
    },
  };
}
